type CurrencyType = 'BDT' | 'USD' | 'EUR' | 'GBP' | 'JPY' | 'INR' | 'CNY';

interface FormatNumberOptions {
  currency?: CurrencyType;
  decimals?: number;
  locale?: string;
  compact?: boolean;
}

/*
 * Usage Examples:
 * formatNumber(20198372)                           // "20,198,372"
 * formatNumber(29382.887)                          // "29,382.89"
 * formatNumber(20198372, { currency: 'BDT' })     // "৳20,198,372.00"
 * formatNumber(29382.887, { currency: 'USD' })    // "$29,382.89"
 * formatNumber(20198372, { compact: true })       // "20.2M"
 */
/**
 * Format number with various options
 * @param number - Number to format (e.g., 20198372 or 29382.887)
 * @param options - Formatting options
 * @returns Formatted number string
 */
export const formatNumber = (
  number: number,
  options?: FormatNumberOptions,
): string => {
  const {
    currency,
    decimals = 2,
    locale = 'en-US',
    compact = false,
  } = options || {};

  // Handle invalid numbers
  if (!isFinite(number) || isNaN(number)) {
    return '0';
  }

  // Compact format (K, M, B, T)
  if (compact) {
    return formatCompact(number, decimals);
  }

  // Currency format
  if (currency) {
    return formatWithCurrency(number, currency, locale, decimals);
  }

  // Default number format
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: number % 1 === 0 ? 0 : decimals,
    maximumFractionDigits: decimals,
  }).format(number);
};

/*
 * Usage Examples:
 * formatWithCurrency(20198372, 'BDT')     // "৳20,198,372.00"
 * formatWithCurrency(29382.887, 'USD')    // "$29,382.89"
 */
/**
 * Format number with currency
 * @param number - Number to format
 * @param currency - Currency type
 * @param locale - Locale string
 * @param decimals - Decimal places
 */
const formatWithCurrency = (
  number: number,
  currency: CurrencyType,
  locale: string,
  decimals: number,
): string => {
  // Custom formatting for BDT (Bengali Taka)
  if (currency === 'BDT') {
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(number);
    return `৳${formatted}`;
  }

  // Standard currency formatting
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);
};

/*
 * Usage Examples:
 * formatCompact(20198372)       // "20.2M"
 */
/**
 * Format number in compact notation (K, M, B, T)
 * @param number - Number to format
 * @param decimals - Decimal places
 */
const formatCompact = (number: number, decimals: number): string => {
  const abbreviations = [
    { value: 1e12, symbol: 'T' },
    { value: 1e9, symbol: 'B' },
    { value: 1e6, symbol: 'M' },
    { value: 1e3, symbol: 'K' },
  ];

  for (const { value, symbol } of abbreviations) {
    if (Math.abs(number) >= value) {
      return `${(number / value).toFixed(decimals)}${symbol}`;
    }
  }

  return number.toFixed(decimals);
};

/*
 * Usage Examples:
 * formatPercentage(0.1234) // "12.34%"
 */
/**
 * Format number as percentage
 * @param number - Number to format (0.1234 for 12.34%)
 * @param decimals - Decimal places (default: 2)
 */
export const formatPercentage = (
  number: number,
  decimals: number = 2,
): string => {
  return `${(number * 100).toFixed(decimals)}%`;
};

/*
 * Usage Examples:
 * formatWithSuffix(1234.56, 'kg') // "1,234.56 kg"
 */
/**
 * Format number with custom suffix
 * @param number - Number to format
 * @param suffix - Suffix to add (e.g., 'kg', 'USD', 'items')
 * @param decimals - Decimal places
 */
export const formatWithSuffix = (
  number: number,
  suffix: string,
  decimals: number = 2,
): string => {
  const formatted = formatNumber(number, { decimals });
  return `${formatted} ${suffix}`;
};

/**
 * Format number with custom prefix
 * @param number - Number to format
 * @param prefix - Prefix to add (e.g., '+', '-', '~')
 * @param decimals - Decimal places
 */
export const formatWithPrefix = (
  number: number,
  prefix: string,
  decimals: number = 2,
): string => {
  const formatted = formatNumber(number, { decimals });
  return `${prefix}${formatted}`;
};

/*
 * Usage Examples:
 * formatRange(1000, 5000) // "1,000 - 5,000"
 * formatRange(1000, 5000, { currency: 'BDT' }) // "৳1,000.00 - ৳5,000.00"
 */
/**
 * Format number range
 * @param min - Minimum number
 * @param max - Maximum number
 * @param options - Formatting options
 */
export const formatRange = (
  min: number,
  max: number,
  options?: FormatNumberOptions,
): string => {
  const formattedMin = formatNumber(min, options);
  const formattedMax = formatNumber(max, options);
  return `${formattedMin} - ${formattedMax}`;
};

/*
 * Usage Examples:
 * formatWithSign(1234.56) // "+1,234.56"
 * formatWithSign(-1234.56) // "-1,234.56"
 */
/**
 * Format number with sign (positive/negative)
 * @param number - Number to format
 * @param options - Formatting options
 */
export const formatWithSign = (
  number: number,
  options?: FormatNumberOptions,
): string => {
  const formatted = formatNumber(Math.abs(number), options);
  const sign = number >= 0 ? '+' : '-';
  return `${sign}${formatted}`;
};

/*
 * Usage Examples:
 * parseNumber("1,234.56") // 1234.56
 * parseNumber("৳1,234.56") // 1234.56
 */
/**
 * Parse formatted number string back to number
 * @param value - Formatted string (e.g., "1,234.56" or "৳1,234.56")
 */
export const parseNumber = (value: string): number => {
  // Remove all non-numeric characters except decimal point and minus sign
  const cleaned = value.replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/*
 * Usage Examples:
 * isValidNumber("1,234.56") // true
 * isValidNumber("abc") // false
 */
/**
 * Validate if value is a valid number
 * @param value - Value to validate
 */
export const isValidNumber = (value: any): boolean => {
  return !isNaN(parseFloat(value)) && isFinite(value);
};
